import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import type { PublicDocumentVerificationResponse } from '../services/api/types/document-verifications';

function kindLabel(kind?: string | null): string {
  if (!kind) return 'Document';
  return kind.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function formatWhen(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

export function VerifyDocumentPage() {
  const { code } = useParams<{ code: string }>();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<PublicDocumentVerificationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      setResult(null);
      const trimmed = (code || '').trim();
      if (!trimmed) {
        setError('Missing verification code.');
        setLoading(false);
        return;
      }
      try {
        const resp = await apiService.verifyDocumentPublic(trimmed);
        if (cancelled) return;
        setResult(resp.data ?? null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Verification failed.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const valid = result?.valid === true;

  return (
    <div className="min-h-screen bg-secondary-50 flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-lg bg-white border border-secondary-200 rounded-xl shadow-sm p-6 space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
            Document verification
          </p>
          <h1 className="text-xl font-semibold text-secondary-900 mt-1">
            {loading ? 'Checking…' : valid ? 'Official document verified' : 'Not verified'}
          </h1>
        </div>

        {loading ? (
          <p className="text-sm text-secondary-600">Looking up verification code…</p>
        ) : null}

        {error ? (
          <p className="text-sm text-rose-800 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
            {error}
          </p>
        ) : null}

        {result ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              valid
                ? 'bg-emerald-50 border-emerald-200 text-emerald-950'
                : 'bg-rose-50 border-rose-200 text-rose-950'
            }`}
          >
            <p className="font-medium">{result.message}</p>
            <p className="mt-2 font-mono text-xs tracking-wide">{result.verification_code}</p>
          </div>
        ) : null}

        {result?.valid ? (
          <dl className="grid grid-cols-1 gap-2 text-sm">
            <div className="flex justify-between gap-3 border-b border-secondary-100 py-2">
              <dt className="text-secondary-500">School</dt>
              <dd className="font-medium text-secondary-900 text-right">
                {result.school_name || '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-3 border-b border-secondary-100 py-2">
              <dt className="text-secondary-500">Document</dt>
              <dd className="font-medium text-secondary-900 text-right">
                {kindLabel(result.document_kind)}
              </dd>
            </div>
            <div className="flex justify-between gap-3 border-b border-secondary-100 py-2">
              <dt className="text-secondary-500">Student</dt>
              <dd className="font-medium text-secondary-900 text-right">
                {result.student_name || '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-3 border-b border-secondary-100 py-2">
              <dt className="text-secondary-500">Admission No.</dt>
              <dd className="font-medium text-secondary-900 text-right">
                {result.admission_no || '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-3 border-b border-secondary-100 py-2">
              <dt className="text-secondary-500">Class</dt>
              <dd className="font-medium text-secondary-900 text-right">
                {result.class_name || '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-3 border-b border-secondary-100 py-2">
              <dt className="text-secondary-500">Period</dt>
              <dd className="font-medium text-secondary-900 text-right">
                {result.billing_period || '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-3 border-b border-secondary-100 py-2">
              <dt className="text-secondary-500">Amount</dt>
              <dd className="font-medium text-secondary-900 text-right">
                {result.amount_label || '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-3 border-b border-secondary-100 py-2">
              <dt className="text-secondary-500">Issued</dt>
              <dd className="font-medium text-secondary-900 text-right">
                {formatWhen(result.issued_at)}
              </dd>
            </div>
            <div className="flex justify-between gap-3 py-2">
              <dt className="text-secondary-500">Fingerprint</dt>
              <dd className="font-mono text-xs text-secondary-800 text-right">
                {result.content_fingerprint || '—'}
              </dd>
            </div>
          </dl>
        ) : null}

        <p className="text-xs text-secondary-500 leading-relaxed">
          If the printed code does not match school records, treat the paper as unofficial. Contact
          the school office to confirm fee dues or payments.
        </p>
      </div>
    </div>
  );
}
